import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { RotateCw, Link2, Save, Loader2, StickyNote, History, Printer, Send } from 'lucide-react';
import { ouvrirPdf } from './recus/ouvrirPdf';
import SepaMiniAgenda from './SepaMiniAgenda';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RELANCE_LABELS = {
  resignature: 'Re-signature demandée',
  lien_paiement_cb: 'Lien de paiement CB envoyé',
  renvoi_lien_signature: 'Lien de signature renvoyé',
  email_relance: 'Relance email',
  echeancier_envoye: 'Échéancier envoyé',
};

/** Extras par plan : re-signature, lien CB, historique des relances, note interne, agenda. */
export default function SepaPlanExtras({ plan, onChanged }) {
  const { getAuthHeader } = useAuth();
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState(plan.note || '');
  const [events, setEvents] = useState([]);
  const headers = useCallback(() => ({ headers: getAuthHeader() }), [getAuthHeader]);
  const sid = plan.souscription_id;

  const fetchAgenda = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/sepa-stripe/admin/plans/${sid}/agenda`, headers());
      setEvents(r.data?.events || []);
    } catch (e) {
      // agenda non bloquant
    }
  }, [sid, headers]);

  useEffect(() => { fetchAgenda(); }, [fetchAgenda]);

  const call = async (action, url, successMsg, copyUrl = false) => {
    setBusy(action);
    try {
      const r = await axios.post(url, {}, headers());
      toast.success(successMsg);
      if (copyUrl && r.data?.url) {
        window.navigator.clipboard?.writeText(r.data.url).catch(() => {});
        toast.info('Lien copié dans le presse-papiers');
      }
      await fetchAgenda();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action impossible');
    } finally {
      setBusy(null);
    }
  };

  const saveNote = async () => {
    setBusy('note');
    try {
      const r = await axios.put(`${API}/sepa-stripe/admin/plans/${sid}/note`, { note }, headers());
      toast.success('Note enregistrée');
      plan.note_updated_at = r.data?.note_updated_at;
      plan.note_updated_by = r.data?.note_updated_by;
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement note');
    } finally {
      setBusy(null);
    }
  };

  const relances = plan.relances || [];

  return (
    <div className="mt-4 space-y-4" data-testid={`sepa-extras-${sid}`}>
      {/* Actions relances */}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          onClick={() => call('resign', `${API}/sepa-stripe/admin/plans/${sid}/resignature`, 'Nouveau lien de signature généré et envoyé', true)}
          disabled={busy !== null}
          className="bg-violet-600 hover:bg-violet-700 text-white"
          data-testid={`sepa-resignature-${sid}`}
        >
          {busy === 'resign' ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RotateCw className="h-4 w-4 mr-1.5" />}
          Relancer re-signature
        </Button>
        <Button
          size="sm"
          onClick={() => call('link', `${API}/sepa-stripe/admin/plans/${sid}/send-payment-link`, 'Lien de paiement CB envoyé au membre', true)}
          disabled={busy !== null}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid={`sepa-send-link-${sid}`}
        >
          {busy === 'link' ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Link2 className="h-4 w-4 mr-1.5" />}
          Envoyer lien de paiement
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={async () => { setBusy('print'); await ouvrirPdf(`${API}/sepa-stripe/admin/plans/${sid}/echeancier-pdf`, getAuthHeader(), `Echeancier_${sid}.pdf`); setBusy(null); }}
          disabled={busy !== null}
          className="border-[#0a3d62] text-[#0a3d62] hover:bg-sky-50"
          data-testid={`sepa-imprimer-echeancier-${sid}`}
        >
          {busy === 'print' ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Printer className="h-4 w-4 mr-1.5" />}
          Imprimer l'échéancier
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => { if (window.confirm("Envoyer l'échéancier PDF au membre par e-mail ?")) call('send-ech', `${API}/sepa-stripe/admin/plans/${sid}/echeancier/envoyer`, 'Échéancier envoyé au membre'); }}
          disabled={busy !== null}
          className="border-amber-400 text-amber-800 hover:bg-amber-50"
          data-testid={`sepa-envoyer-echeancier-${sid}`}
        >
          {busy === 'send-ech' ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
          Envoyer au membre
        </Button>
      </div>

      {/* Historique des relances */}
      {relances.length > 0 && (
        <div className="bg-violet-50/60 border border-violet-100 rounded-xl p-3" data-testid={`sepa-relances-${sid}`}>
          <p className="text-[11px] font-bold text-violet-800 uppercase tracking-wider flex items-center gap-1.5 mb-2">
            <History className="h-3.5 w-3.5" /> Historique des relances ({relances.length})
          </p>
          <div className="space-y-1">
            {relances.slice().reverse().map((r, i) => (
              <p key={i} className="text-[11px] text-slate-600">
                {String(r.at || '').slice(0, 16).replace('T', ' ')} → <strong>{RELANCE_LABELS[r.type] || r.type}</strong>
                {r.by ? <span className="text-slate-400"> par {r.by}</span> : null}
                {r.detail ? <span className="text-slate-400"> · {r.detail}</span> : null}
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Note interne */}
        <div className="bg-amber-50/60 border border-amber-100 rounded-xl p-3" data-testid={`sepa-note-${sid}`}>
          <p className="text-[11px] font-bold text-amber-800 uppercase tracking-wider flex items-center gap-1.5 mb-2">
            <StickyNote className="h-3.5 w-3.5" /> Note
          </p>
          <textarea
            className="w-full border border-amber-200/70 rounded-lg p-2 text-xs bg-white min-h-[90px]"
            placeholder="Note interne (suivi téléphonique, accord de paiement, contexte…)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid={`sepa-note-input-${sid}`}
          />
          <div className="flex items-center justify-between mt-2">
            <p className="text-[10px] text-slate-400">
              {plan.note_updated_at
                ? `Dernière modif : ${String(plan.note_updated_at).slice(0, 16).replace('T', ' ')}${plan.note_updated_by ? ` par ${plan.note_updated_by}` : ''}`
                : 'Aucune note enregistrée'}
            </p>
            <Button size="sm" onClick={saveNote} disabled={busy !== null} className="bg-[#DEA900] hover:bg-[#f5cc3e] text-[#0a3d62] h-7 text-[11px]" data-testid={`sepa-note-save-${sid}`}>
              {busy === 'note' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
              Enregistrer
            </Button>
          </div>
        </div>

        {/* Agenda */}
        <SepaMiniAgenda
          events={events}
          testid={`sepa-agenda-${sid}`}
          onAdd={async (date, label) => {
            try {
              await axios.post(`${API}/sepa-stripe/admin/plans/${sid}/agenda`, { date, label }, headers());
              await fetchAgenda();
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Erreur ajout événement');
            }
          }}
          onMove={async (evId, newDate) => {
            const ev = events.find((x) => x.id === evId);
            if (!ev || ev.date === newDate) return;
            try {
              await axios.put(`${API}/sepa-stripe/admin/agenda/${evId}`, { date: newDate, label: ev.label }, headers());
              await fetchAgenda();
            } catch (e) {
              toast.error('Déplacement impossible');
            }
          }}
          onDelete={async (ev) => {
            try {
              await axios.delete(`${API}/sepa-stripe/admin/agenda/${ev.id}`, headers());
              await fetchAgenda();
            } catch (e) {
              toast.error('Suppression impossible');
            }
          }}
        />
      </div>
    </div>
  );
}
